import React, { PropsWithChildren, RefObject, useEffect, useState } from 'react';
import { Stack, Switch } from '@mui/material';

import { MaterialPropertyValuePair, usePrevious } from '@paragon/client-tools';

interface Props<T> extends PropsWithChildren {
  property: string;
  value: T | null;
  disabled?: boolean;

  inputRef?: RefObject<HTMLInputElement | null>;
  onMadeNull: () => void;
}

/**
 * Allows any type of input to be made null via a switch. Ideally each type of input used would support this operation
 *   natively which provide a better user experience and probably cleaner code.
 */
export function OptionallyNullableFieldWrapper<T>({
  property,
  value,
  disabled,
  inputRef,
  onMadeNull,
  children,
}: Props<T>) {
  const [isEscapingNull, setIsEscapingNull] = useState(false);
  const wasEscapingNull = usePrevious(isEscapingNull);

  if (isEscapingNull && value != null) {
    setIsEscapingNull(false);
  }

  useEffect(() => {
    if (isEscapingNull && !wasEscapingNull) {
      inputRef?.current?.focus();
    }
  }, [isEscapingNull, wasEscapingNull, inputRef]);

  return (
    <Stack direction="row" sx={{ alignItems: 'baseline' }}>
      <Switch
        disabled={disabled}
        checked={value != null || isEscapingNull}
        onClick={() => {
          if (value != null) {
            onMadeNull();
          } else {
            setIsEscapingNull((escaping) => !escaping);
          }
        }}
      />

      {value == null && !isEscapingNull ? <MaterialPropertyValuePair property={property} value="None" /> : children}
    </Stack>
  );
}
